import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { auth } from '../firebase';
import { setContext } from 'apollo-link-context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import config from 'config';
import getAlgoliaLink from '../AlgoliaLink';
import { history } from '../index';
import { PATH } from 'routes/constant';
import { SEARCH_STYLE_TYPE } from './search/search_style';
import { trackEvent, TRACK_EVENT } from 'utils/analytics';
import jwt_decode from 'jwt-decode';
import { get, isEmpty } from 'lodash';
import utils, { bugSnagReport } from 'utils/utils';
import { API_ERROR_MESSAGE, LOCAL_STORAGE_KEY } from 'utils/constant';

const TIME_TO_REFRESH_TOKEN = 5 * 60 * 1000; // 5 minutes in milliseconds
function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''),
    );

    return JSON.parse(jsonPayload);
}

const getCurrentUserAuthToken = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
        const token = await currentUser.getIdToken();
        const decodedToken: any = jwt_decode(token);
        const { exp } = decodedToken;
        const currentTimestamp = Date.now();
        // forceRefresh token if token expires in next 5 minutes or has already expired
        if (exp * 1000 - currentTimestamp < TIME_TO_REFRESH_TOKEN) {
            const newToken = await currentUser.getIdToken(true);
            console.debug(`old token expired at: ${exp}`);
            console.debug(`issued newToken`);
            return newToken;
        }
        return token;
    } else {
        return null;
    }
};

const authMiddleware: any = setContext((_, { headers }) => {
    return getCurrentUserAuthToken().then((token) => {
        //console.debug("Token======", token);
        return {
            headers: {
                ...headers,
                Authorization: token ? token : '',
            },
        };
    });
});

const serverLink: HttpLink = new HttpLink({
    uri: config.graphqlUrl,
    credentials: 'include',
});
const notificationLink: HttpLink = new HttpLink({
    uri: config.notificationGraphqlUrl,
    credentials: 'include',
});

const graphqlLink = ApolloLink.split(
    (operation) => operation.getContext().clientName === 'notification',
    notificationLink,
    serverLink,
);
const awareAuthenticate = new ApolloLink((operation: any, forward: any) => {
    // const { operationName } = operation;
    const context = operation.getContext();
    const headers = context.headers;
    if (context?.scope === 'private' && !headers.Authorization) {
        throw new Error('Access denied');
    }
    if (headers.Authorization) {
        const decoded = parseJwt(headers.Authorization);
        if (context?.approved && !decoded.approved) {
            throw Error('__HIDDEN__User needs to be approved');
        }
    }
    if (!headers.Authorization && context.allowAnonymousAuth) {
        const localUserStr = localStorage.getItem(LOCAL_STORAGE_KEY.LOCAL_USER_KEY);
        const localUser = localUserStr ? JSON.parse(localUserStr) : null;
        if (localUser?.id) {
            headers.Anonymous = localUser?.id;
        }
    }
    return forward(operation);
});

const MAX_RETRY_COUNT = 10;
const retryLink = new RetryLink({
    delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
    },
    attempts: (count, operation, error) => {
        // if (!error.message || (error.message !== 'Failed to fetch' && error.name !== 'ServerParseError')) {
        //     // If error is not related to connection, do not retry
        //     return false
        // }
        const shouldRetry = count <= MAX_RETRY_COUNT;
        if (shouldRetry) {
            console.debug(`retry ${operation.operationName}: ${count} time(s)`);
            console.debug(operation.variables);
        }
        operation.setContext((context) => ({ ...context, retryCount: count }));
        return shouldRetry;
    },
});

let client: ApolloClient<any>;

let randomSortNumber = Math.random();

const applyRandomSort = (items) => {
    let clonedItems = [...items];
    for (let index = clonedItems.length - 1; index > 0; index--) {
        let randomIndex = Math.floor(randomSortNumber * index);
        let currentIndexItem = clonedItems[index];
        clonedItems[index] = clonedItems[randomIndex];
        clonedItems[randomIndex] = currentIndexItem;
    }
    return clonedItems;
};

export const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                locationsInRadius: {
                    keyArgs: ['$filters', '$index', '$query', '$facets'],
                    merge(existing = {}, _incoming = {}, { variables }) {
                        const incoming =
                            _incoming?.__typename === SEARCH_STYLE_TYPE.algolia_styles_recommend
                                ? {
                                    ..._incoming,
                                    ...(_incoming?.hits
                                        ? {
                                            hits: applyRandomSort(_incoming?.hits),
                                        }
                                        : {}),
                                }
                                : _incoming;
                        if (variables && !variables.page) {
                            return incoming;
                        }

                        if (existing.length === 0) {
                            return incoming;
                        }
                        return {
                            ...incoming,
                            hits: [...(existing.hits ?? []), ...incoming.hits],
                        };
                    },
                },
                algoliaGetUsers: {
                    keyArgs: ['$filters', '$index', '$query', '$facets'],
                    merge(existing = {}, incoming = {}, { variables }) {
                        if (variables && !variables.page) {
                            return incoming;
                        }

                        if (isEmpty(existing) || existing.length === 0) {
                            return incoming;
                        }
                        return {
                            ...incoming,
                            hits: [...existing.hits, ...incoming.hits],
                        };
                    },
                },
                algoliaGetAllFeedbacks: {
                    keyArgs: ['$filters', '$index', '$query', '$facets'],
                    merge(existing = {}, incoming = {}, { variables }) {
                        if (variables && !variables.page) {
                            return incoming;
                        }

                        if (isEmpty(existing) || existing.length === 0) {
                            return incoming;
                        }
                        return {
                            ...incoming,
                            hits: [...existing.hits, ...incoming.hits],
                        };
                    },
                },
                myStyle: {
                    keyArgs: false,
                    merge(existing = {}, _incoming = {}, { variables }) {
                        const incoming = _incoming;
                        console.log('_incoming', existing, _incoming);
                        if (variables && !variables.input.paging.page) {
                            return incoming;
                        }

                        if (existing.length === 0) {
                            return incoming;
                        }
                        return {
                            ...incoming,
                            data: [...existing.data, ...incoming.data],
                        };
                    },
                },
                items: {
                    keyArgs: false,
                    merge(existing = {}, _incoming = {}, { variables }) {
                        const incoming = _incoming;
                        if (variables && !variables.page) {
                            return incoming;
                        }

                        if (existing.length === 0) {
                            return incoming;
                        }
                        return {
                            ...incoming,
                            hits: [...existing.hits, ...incoming.hits],
                        };
                    },
                },
                getMySessions: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        OrderItemResponse: {
            keyFields: ['id', 'size', 'color'],
        },
    },
});

const getApolloClient = () => {
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        let headersBg = null,
            retryCount = 0,
            shouldLoginAgain = false;
        const operationContext = operation.getContext();
        const headers = operationContext.headers;
        const host = window.location.host;
        if (networkError) {
            if (headers.background) {
                headersBg = headers.background;
            }
            retryCount = operationContext.retryCount || 0;
        }

        const trackOperation: any = {
            operationName: operation.operationName,
            variables: operation.variables,
            background: headersBg,
            retryCount,
        };
        if (retryCount > MAX_RETRY_COUNT) {
            trackOperation.failedToRetry = true;
        }
        trackEvent(graphQLErrors ? TRACK_EVENT.GRAPHQL_ERRORS : TRACK_EVENT.NETWORK_ERROR, {
            graphQLErrors,
            networkError,
            host,
            operation: trackOperation,
        });
        if (graphQLErrors) {
            graphQLErrors.map(({ message, extensions, locations, path }) => {
                if (!['auth/email-already-exists'].includes(get(extensions, ['exception', 'errorInfo', 'code']))) {
                    console.error(
                        `[GraphQL error]:: Operation=${operation.operationName},
                      Variables=${JSON.stringify(operation.variables)},
                      Message=${message},
                      Code=${extensions?.code},
                      Locations=${JSON.stringify(locations)},
                      Path=${path}`,
                    );
                    bugSnagReport(message, operation.operationName, operation.variables, auth.currentUser?.uid);
                }
                if (utils.isJson(message)) {
                    const parsedErr = JSON.parse(message);
                    if (parsedErr?.error === API_ERROR_MESSAGE.API_LIMIT_EXCEEDED) {
                        setTimeout(() => {
                            return forward(operation);
                        }, parsedErr?.refetchIn);
                    }
                }

                if (extensions?.code === 'UNAUTHENTICATED') {
                    shouldLoginAgain = true;
                }
                return null;
            });
        }

        if (shouldLoginAgain) {
            // in case refresh token has been revoked
            const currentUser = auth.currentUser;
            if (currentUser) {
                auth.signOut();
            }
            history?.push(PATH.LOGIN);
            return;
        }

        if (networkError) {
            if (headers.background) {
                console.debug('skip 500 for api: ', operation.operationName);
                return;
            }
            if (
                !operationContext.hasOwnProperty('retryCount') ||
                (operationContext.hasOwnProperty('retryCount') && operationContext.retryCount > MAX_RETRY_COUNT)
            ) {
                history?.push(PATH.SERVER_ERROR);
            }
            bugSnagReport(networkError, operation?.operationName, operation?.variables, auth?.currentUser?.uid);
            console.error(`[Network error]: ${operation.operationName}, ${networkError}`);
        }
    });
    if (!client) {
        try {
            client = new ApolloClient({
                link: ApolloLink.from([
                    getAlgoliaLink(),
                    authMiddleware,
                    awareAuthenticate,
                    errorLink,
                    retryLink,
                    graphqlLink,
                ]),
                cache: cache,
                // connectToDevTools: process.env.NODE_ENV === "development",
            });
        } catch (err) {
            console.log('err', err);
        }
    }
    return client;
};
export default getApolloClient;
